import React, { useState, useEffect } from 'react';

import Icon from 'components/Icons/Icon';
import { Tooltip } from 'components/Common/Tooltip';

import './UnreadMessagesIcon.scss';

// keep the exact same positioning / appearance as the original sticky chat icon
const ICON_STYLE = {
  position: 'absolute',
  top: 0,
  right: '-7px',
  color: 'whitesmoke',
};

// Box spanning the ~30px side-pane toggle strip, used to horizontally centre
// the badge over it. The strip sits ~15px beyond this sticky container's right
// edge (the container is inset by the fluid container's padding, while the
// strip's row cancels that padding with negative margins); 30px = strip width.
const BADGE_WRAP_STYLE = {
  position: 'absolute',
  top: 0,
  right: '-15px',
  width: '30px',
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
};

/**
 * Sticky chat indicator. Renders the "fa-comments" icon by default; when there
 * are unread messages it swaps to a circular badge containing the unread
 * count, fading in / out with the same animation as SubmissionStatusIcon
 * (the ssi-fade-in / ssi-fade-out keyframes).
 */
function UnreadMessagesIcon({ count = 0 }) {
  // 'hidden' -> comments icon; 'in' / 'out' -> badge (fading in / out)
  const [phase, setPhase] = useState('hidden');
  // remembered so the badge keeps its number while fading out (count is 0 then)
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    if (count > 0) {
      setDisplayCount(count);
      setPhase('in');
    } else {
      // fade the badge out if it was showing, otherwise stay hidden
      setPhase((prev) => (prev === 'hidden' ? 'hidden' : 'out'));
    }
  }, [count]);

  useEffect(() => {
    if (phase !== 'out') return;
    // matches the ssi-fade-out duration
    const timer = setTimeout(() => setPhase('hidden'), 300);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === 'in' || phase === 'out') {
    return (
      <span style={BADGE_WRAP_STYLE}>
        <Tooltip
          placement="left"
          description={`You have ${displayCount} unread notifications`}
        >
          <span
            className={`unread-messages-badge ${phase === 'out' ? 'is-exiting' : ''}`}
            role="status"
            aria-label={`${displayCount} unread message${displayCount === 1 ? '' : 's'}`}
          >
            {displayCount > 99 ? '99+' : displayCount}
          </span>
        </Tooltip>
      </span>
    );
  }

  return <Icon className="fas fa-comments" style={ICON_STYLE} />;
}

export default UnreadMessagesIcon;
